import { SearchResult } from '@/models/Repository'
import AccountApi from '@/api/account'
import MasterApi from '@/api/master'
// import SystemOwnerApi from '@/api/SystemOwner'
// import AgencyApi from '@/api/agency'
// import AdvertiserApi from '@/api/AdvertiserApi'

const getErrorMessage = rowErrMessage => {
  switch (rowErrMessage) {
    case 'this email is already used':
      return '入力されたメールアドレスはすでに登録されています。'
    default:
      return '保存に失敗しました。'
  }
}

export class AccountRepository {
  static async getList() {
    const res = await AccountApi.getList()
    return res.items
  }

  /**
   * 広告主で絞り込む
   * @param {number} advertiserId
   * return {Account[]}
   */
  static async getListByAdvertiser(advertiserId) {
    const res = await AccountApi.search({ advertiserId })
    return res.items.map(item => new Account(item))
  }
}

export class Account {
  constructor({
    token,
    id,
    name,
    email,
    belongType,
    belongId,
    belongName,
    userType,
    systemOwnerId,
    status,
    permission,
  }) {
    this.token = token || undefined
    this.id = Number(id) || undefined
    this.name = name || undefined
    this.email = email || undefined
    this.belongType = belongType || undefined
    this.belongId = belongId || undefined
    this.belongName = belongName || undefined
    this.userType = userType || undefined
    this.systemOwnerId = systemOwnerId || undefined
    this.status = status || undefined
    this.permission = permission || undefined
  }
  get belongTypeName() {
    switch (this.belongType) {
      case 'system owner':
        return 'システムオーナー'
      case 'agency':
        return '代理店'
      case 'advertiser':
        return '広告主'
      default:
        return ''
    }
  }
  get userTypeName() {
    switch (this.userType) {
      case 'sales':
        return '営業'
      case 'sales manager':
        return '営業マネージャー'
      case 'operator':
        return '運用'
      case 'operator manager':
        return '運用マネージャー'
      case 'administrator':
        return '管理者'
      case 'system operator':
        return 'システム管理者'
      case 'super admin':
        return 'スーパーシステム管理者'
      default:
        return ''
    }
  }
  get isSuperAdmin() {
    return this.userType === 'super admin'
  }
  get isSystemOwner() {
    return this.belongType === 'system owner'
  }
  get isAgency() {
    return this.belongType === 'agency'
  }
  get isAdvertiser() {
    return this.belongType === 'advertiser'
  }
  get canEdit() {
    return Boolean(
      this.permission === 'readonly' ||
        this.permission === 'operator' ||
        this.permission === 'admin',
    )
  }
  get canChangePermission() {
    return this.permission === 'admin'
  }
  // API
  static async search({
    keyword,
    belongType,
    systemOwnerId,
    agencyId,
    advertiserId,
    pageSize,
    pageNo,
  } = {}) {
    const query = { keyword, belongType, systemOwnerId, agencyId, advertiserId }
    const searchResult = await AccountApi.search(query, pageSize, pageNo)
    const list = assignModel(searchResult)
    return new SearchResult({
      count: searchResult.count || 0,
      pageNo: searchResult.pageNo || 1,
      items: list,
      pageSize: pageSize || 20,
    })
  }

  static async isHideRevenue(userBelongType, userBelongId, userType) {
    if (userBelongType !== 'advertiser') return false

    const isHideRevenueAdvertiser = await MasterApi.isHideRevenue(userBelongId)

    if (!isHideRevenueAdvertiser.item) return false

    return userType === 'sales' || userType === 'sales manager'
  }
}

const assignModel = data => {
  return data.items.map(o => new Account(o))
}

export class EditAccountForm {
  constructor(item = {}) {
    const { id, userType, name, status } = item
    this.isSystemOwner = item.isSystemOwner
    this.id = Number(id) || undefined
    this.userType = userType || undefined
    this.name = name || undefined
    this.status = status || undefined
  }
  get validation() {
    return Boolean(this.userType) && Boolean(this.name)
  }
  async execEdit() {
    const { name, status } = this
    const userType = this.isSystemOwner ? undefined : this.userType
    let params = {
      userType,
      name,
      status,
    }
    const res = await AccountApi.edit(this.id, params)
    return res
  }
}

export class InviteAccountForm {
  constructor(item = {}) {
    const {
      belongType,
      belongName,
      systemOwnerId,
      agencyId,
      advertiserId,
      userType,
      name,
      email,
      emailConfirm,
    } = item
    this.belongType = belongType || undefined
    this.belongName = belongName || undefined
    this.systemOwnerId = systemOwnerId || undefined
    this.agencyId = agencyId || undefined
    this.advertiserId = advertiserId || undefined
    this.userType = userType || undefined
    this.name = name || undefined
    this.email = email || undefined
    this.emailConfirm = emailConfirm || undefined
    // UI制御用
    this.permission = undefined
  }
  get validation() {
    if (
      (!this.systemOwnerId && !this.agencyId && !this.advertiserId) ||
      (this.belongType !== 'systemOwner' && !this.userType)
    ) {
      return false
    }
    return Boolean(this.emailConfirmStatus && this.name)
  }
  get belongSelectedId() {
    switch (this.belongType) {
      case 'systemOwner':
        return this.systemOwnerId
      case 'agency':
        return this.agencyId
      case 'advertiser':
        return this.advertiserId
      default:
        return ''
    }
  }
  get emailConfirmStatus() {
    if (!this.email || !this.emailConfirm) return false
    if (this.email.indexOf('@') === -1) return false
    return this.email === this.emailConfirm
  }
  async execInvite() {
    let params = {
      email: this.email,
      name: this.name,
      userType:
        this.belongType === 'systemOwner' ? 'system operator' : this.userType,
    }
    const systemOwnerId =
      this.belongType === 'systemOwner' ? this.systemOwnerId : undefined
    const agencyId = this.belongType === 'agency' ? this.agencyId : undefined
    const advertiserId =
      this.belongType === 'advertiser' ? this.advertiserId : undefined
    try {
      const res = await AccountApi.invite(
        params,
        systemOwnerId,
        agencyId,
        advertiserId,
      )
      return res
    } catch (e) {
      throw new Error(getErrorMessage(e.response.data.message))
    }
  }
}

export class RegisterAccountForm {
  constructor(item = {}) {
    const {
      token,
      name,
      password,
      passwordConfirm,
      email,
      belongType,
      belongName,
    } = item
    this.token = token || undefined
    this.name = name || ''
    this.password = password || ''
    this.passwordConfirm = passwordConfirm || ''
    this.email = email || undefined
    this.belongType = belongType || undefined
    this.belongName = belongName || undefined
  }
  get validation() {
    return this.validateName && this.validatePassword
  }
  get validateName() {
    return this.name.length > 0
  }
  get validatePassword() {
    // 半角英字と数字両方を含む8文字以上
    const regex = /^(?=.*?[a-zA-Z])(?=.*?\d)[a-zA-Z\d]{8,}$/
    return this.password === this.passwordConfirm && regex.test(this.password)
  }
  static async getInvitedUser(token) {
    const res = await AccountApi.getInvitedUser(token)
    return res
  }
  async execRegister() {
    const res = await AccountApi.register(this)
    return res
  }
}
